/**
 * The page's settings and the other values it keeps in this browser.
 *
 * A module declares each of its settings once, with `defineSetting`, and reads
 * and writes it through the returned handle. The settings tab lists the
 * declarations. No other code calls localStorage; see README.md.
 */

export type SettingValue = boolean | number | string | readonly string[];

interface Common<T extends SettingValue> {
    /** The localStorage key. */
    key: string;
    title: string;
    description: string;
    /** Where the settings tab shows it, outermost first: `["Editor"]`. */
    group: readonly string[];
    default: T;
    /** Its place in the group; lower first, then by title. */
    order?: number;
    /** Read a value stored in an older form, which is rewritten at the next change. */
    legacy?: (raw: string) => T | undefined;
    /** Left out of the settings tab: a knob that a deployment sets in its configuration. */
    hidden?: boolean;
}

export interface BooleanDeclaration extends Common<boolean> {
    type: "boolean";
}

export interface NumberDeclaration extends Common<number> {
    type: "number";
    min?: number;
    max?: number;
    integer?: boolean;
}

export interface StringDeclaration extends Common<string> {
    type: "string";
}

export interface EnumOption<V extends string> {
    value: V;
    label: string;
    description?: string;
}

export interface EnumDeclaration<V extends string = string> extends Common<V> {
    type: "enum";
    options: readonly EnumOption<V>[];
}

/** A list of strings, stored as JSON. The settings tab has no control for a list, so it is hidden. */
export interface ListDeclaration extends Common<readonly string[]> {
    type: "list";
    hidden: true;
}

export type Declaration =
    BooleanDeclaration | NumberDeclaration | StringDeclaration | EnumDeclaration | ListDeclaration;

/** What the settings tab can list. */
export type ListedDeclaration = Exclude<Declaration, ListDeclaration>;

export interface Setting<T extends SettingValue> {
    readonly declaration: Declaration;
    get(): T;
    set(value: T): void;
    /** Back to the default, by removing the stored value. */
    reset(): void;
    /** Whether the user has stored a value that differs from the default. */
    readonly modified: boolean;
    /** Set by the deployment, so the user cannot change it. */
    readonly fixed: boolean;
    /** Called when the value changes, here or in another tab. Returns the unsubscribe. */
    onChange(listener: (value: T) => void): () => void;
}

/** Values the deployment decides, by key; see "Deployments" in README.md. */
export interface Deployment {
    fixed?: Record<string, SettingValue>;
    defaults?: Record<string, SettingValue>;
    hidden?: readonly string[];
}

/** Where values are kept: localStorage in the page, a Map in tests. */
export interface Storage {
    get(key: string): string | undefined;
    /** `undefined` removes the key. */
    set(key: string, value: string | undefined): void;
    /** Report keys that another tab changed. */
    watch(listener: (key: string | undefined) => void): void;
}

interface Entry {
    declaration: Declaration;
    listeners: Set<(value: SettingValue) => void>;
    /** The value last reported to the listeners. */
    last: SettingValue;
}

export class SettingsRegistry {
    readonly #storage: Storage;
    readonly #entries = new Map<string, Entry>();
    readonly #registryListeners = new Set<() => void>();
    #deployment: Deployment = {};

    constructor(storage: Storage) {
        this.#storage = storage;
        storage.watch((key) => {
            if (key === undefined) for (const entry of this.#entries.values()) this.#notify(entry);
            else {
                const entry = this.#entries.get(key);
                if (entry !== undefined) this.#notify(entry);
            }
        });
    }

    define<V extends string>(declaration: EnumDeclaration<V>): Setting<V>;
    define(declaration: BooleanDeclaration): Setting<boolean>;
    define(declaration: NumberDeclaration): Setting<number>;
    define(declaration: StringDeclaration): Setting<string>;
    define(declaration: ListDeclaration): Setting<readonly string[]>;
    define(declaration: Declaration): Setting<SettingValue> {
        if (this.#entries.has(declaration.key)) throw new Error(`setting ${declaration.key} is declared twice`);
        const entry: Entry = { declaration, listeners: new Set(), last: declaration.default };
        this.#entries.set(declaration.key, entry);
        entry.last = this.#value(entry);
        for (const listener of this.#registryListeners) listener();
        return this.#handle(entry);
    }

    /** The declarations a settings tab shows: all but hidden ones and those the deployment hides. */
    declarations(): ListedDeclaration[] {
        const hidden = new Set(this.#deployment.hidden);
        return [...this.#entries.values()]
            .map((entry) => entry.declaration)
            .filter((d): d is ListedDeclaration => d.type !== "list" && d.hidden !== true && !hidden.has(d.key));
    }

    /** The handle of a declared setting, for code that has only its key, such as the settings tab. */
    setting(key: string): Setting<SettingValue> | undefined {
        const entry = this.#entries.get(key);
        return entry === undefined ? undefined : this.#handle(entry);
    }

    /** Called when a setting is declared, so an open settings tab can list it. */
    onDeclare(listener: () => void): () => void {
        this.#registryListeners.add(listener);
        return () => this.#registryListeners.delete(listener);
    }

    applyDeployment(deployment: Deployment): void {
        this.#deployment = deployment;
        for (const entry of this.#entries.values()) this.#notify(entry);
        for (const listener of this.#registryListeners) listener();
    }

    /** A value kept in this browser that is not a setting, such as a token. */
    stored(key: string): StoredValue {
        const storage = this.#storage;
        return {
            get: () => storage.get(key),
            set: (value) => storage.set(key, value),
        };
    }

    #handle(entry: Entry): Setting<SettingValue> {
        const registry = this;
        const key = entry.declaration.key;
        return {
            declaration: entry.declaration,
            get: () => registry.#value(entry),
            set: (value) => registry.#set(entry, value),
            reset: () => registry.#set(entry, registry.#default(entry)),
            get modified() {
                return registry.#stored(entry) !== undefined;
            },
            get fixed() {
                return registry.#deployment.fixed?.[key] !== undefined;
            },
            onChange: (listener) => {
                entry.listeners.add(listener);
                return () => entry.listeners.delete(listener);
            },
        };
    }

    #value(entry: Entry): SettingValue {
        const key = entry.declaration.key;
        const fixed = this.#deployment.fixed?.[key];
        if (fixed !== undefined && valid(entry.declaration, fixed)) return fixed;
        return this.#stored(entry) ?? this.#default(entry);
    }

    #default(entry: Entry): SettingValue {
        const given = this.#deployment.defaults?.[entry.declaration.key];
        return given !== undefined && valid(entry.declaration, given) ? given : entry.declaration.default;
    }

    #stored(entry: Entry): SettingValue | undefined {
        const raw = this.#storage.get(entry.declaration.key);
        if (raw === undefined) return undefined;
        const value = decode(entry.declaration, raw);
        return value === undefined || same(value, this.#default(entry)) ? undefined : value;
    }

    #set(entry: Entry, value: SettingValue): void {
        const { declaration } = entry;
        if (!valid(declaration, value)) throw new Error(`${String(value)} is not a value of ${declaration.key}`);
        // A value equal to the default is not stored, so a later change of the default applies.
        this.#storage.set(declaration.key, same(value, this.#default(entry)) ? undefined : encode(value));
        this.#notify(entry);
    }

    #notify(entry: Entry): void {
        const value = this.#value(entry);
        if (same(value, entry.last)) return;
        entry.last = value;
        for (const listener of entry.listeners) listener(value);
    }
}

export interface StoredValue {
    get(): string | undefined;
    set(value: string | undefined): void;
}

export function valid(declaration: Declaration, value: unknown): value is SettingValue {
    switch (declaration.type) {
        case "boolean":
            return typeof value === "boolean";
        case "string":
            return typeof value === "string";
        case "enum":
            return declaration.options.some((option) => option.value === value);
        case "list":
            return Array.isArray(value) && value.every((item) => typeof item === "string");
        case "number":
            return (
                typeof value === "number" &&
                Number.isFinite(value) &&
                (declaration.integer !== true || Number.isInteger(value)) &&
                (declaration.min === undefined || value >= declaration.min) &&
                (declaration.max === undefined || value <= declaration.max)
            );
    }
}

function same(a: SettingValue, b: SettingValue): boolean {
    if (typeof a !== "object" || typeof b !== "object") return a === b;
    return a.length === b.length && a.every((item, i) => item === b[i]);
}

function encode(value: SettingValue): string {
    return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/** Booleans are stored as `true`/`false`, numbers in decimal, lists as JSON, strings as they are. */
function decode(declaration: Declaration, raw: string): SettingValue | undefined {
    let value: unknown;
    switch (declaration.type) {
        case "boolean":
            value = raw === "true" ? true : raw === "false" ? false : undefined;
            break;
        case "number":
            value = raw.trim() === "" ? undefined : Number(raw);
            break;
        case "list":
            try {
                value = JSON.parse(raw);
            } catch {
                value = undefined;
            }
            break;
        default:
            value = raw;
    }
    if (value !== undefined && valid(declaration, value)) return value;
    const legacy = declaration.legacy?.(raw as never);
    return legacy !== undefined && valid(declaration, legacy) ? legacy : undefined;
}

/** A Map, for tests and for a browser that blocks storage. */
export class MemoryStorage implements Storage {
    readonly #values = new Map<string, string>();

    get(key: string): string | undefined {
        return this.#values.get(key);
    }

    set(key: string, value: string | undefined): void {
        if (value === undefined) this.#values.delete(key);
        else this.#values.set(key, value);
    }

    watch(_listener: (key: string | undefined) => void): void {}
}

/**
 * localStorage, falling back to memory for keys it refuses: storage can be
 * blocked for the site, and a value then lasts as long as the page does.
 */
export class BrowserStorage implements Storage {
    readonly #fallback = new MemoryStorage();

    get(key: string): string | undefined {
        try {
            return localStorage.getItem(key) ?? undefined;
        } catch {
            return this.#fallback.get(key);
        }
    }

    set(key: string, value: string | undefined): void {
        try {
            if (value === undefined) localStorage.removeItem(key);
            else localStorage.setItem(key, value);
        } catch {
            this.#fallback.set(key, value);
        }
    }

    watch(listener: (key: string | undefined) => void): void {
        // Fired in the other tabs of the same origin, not in the one that wrote; a key of null is clear().
        globalThis.addEventListener?.("storage", (event) => listener(event.key ?? undefined));
    }
}

/** The page's settings. The workbench and the page share it, being one document. */
export const settings = new SettingsRegistry(new BrowserStorage());

export const defineSetting: SettingsRegistry["define"] = settings.define.bind(settings);

export function storedValue(key: string): StoredValue {
    return settings.stored(key);
}
